import { trackEvent } from '@fs/zion-analytics'
import { ControlHighlight } from '@fs/zion-icon'
import { Button, IconButton } from '@fs/zion-ui'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useSlsHighlightsContext } from '@fs/zion-sls'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import HighlightPanelsContainer from '../highlightPanelsContainer/HighlightPanelsContainer'
import { getFieldValueText } from '../highlightPanel/utils/highlightUtils'

const highlightButtonCss = css`
  margin-top: -0.5rem;
  margin-inline-end: 0;
  margin-bottom: 0.5rem;
  margin-inline-start: 0.5rem;
`

const HighlightButton = ({
  field,
  editingSharedFieldHighlights,
  onClick = () => {},
  onSave = () => {},
  onCancel = () => {},
  disabled,
  buttonProps,
  iconOnly = false,
  shouldRenderPanel = true,
  splitScreenImageArk,
  saveToSls = true,
}) => {
  const [t] = useTranslation()
  const { restrictedFields } = useCollectionContextProvider()
  const isRestricted = restrictedFields?.includes(FIELDS.HIGHLIGHTS)
  const { setActiveFieldInfo, getHighlights, activeFieldInfo } = useSlsHighlightsContext()
  const isEditing = useMemo(
    () =>
      getHighlights().mainEditHighlights?.some((highlight) => highlight.fieldId === field?.id) ||
      getHighlights().splitScreenEditHighlights?.some((highlight) => highlight.fieldId === field?.id),
    [getHighlights, field?.id]
  )

  // This attempts to get the field from a SLS element in case an intermediate element
  // like NAME_GIVEN, NAME_SURNAME or AGE is passed in
  const parsedField = field?.subElements?.[0] || field

  const processActiveFieldInfo = useCallback(() => {
    // We need to ensure that the field info we set in context has a value property,
    // otherwise the shared highlight panel will break when it tries to read activeFieldInfo.value
    if (!parsedField?.value) {
      // eslint-disable-next-line no-console
      console.error('HighlightButton: field does not have a value property', { field: parsedField })
    }

    const processedFieldValue = {
      ...parsedField,
      value: parsedField.value,
    }
    setActiveFieldInfo({
      ...processedFieldValue,
      displayValue: getFieldValueText(processedFieldValue),
      isFromSlsData: false,
    })
  }, [parsedField, setActiveFieldInfo])

  if (iconOnly) {
    return (
      <>
        <IconButton
          data-testid="edit-highlight-button"
          disabled={editingSharedFieldHighlights || isRestricted || disabled}
          Icon={ControlHighlight}
          title={
            isEditing
              ? t('edit-index.edit-highlight.button', 'Edit Highlight')
              : t('edit-index.add-highlight.button', 'Add Highlight')
          }
          linkName={isEditing ? 'Edit Highlight' : 'Add Highlight'}
          onClick={(event) => {
            trackEvent({
              event_name: 'click_action',
              link_name: `Highlight: Open`,
            })
            onClick(field, event)
            processActiveFieldInfo()
          }}
          {...buttonProps}
        />
        {shouldRenderPanel && activeFieldInfo?.id === parsedField?.id && (
          <HighlightPanelsContainer
            field={field}
            onSave={onSave}
            onCancel={onCancel}
            splitScreenImageArk={splitScreenImageArk}
            saveToSls={saveToSls}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div css={highlightButtonCss}>
        <Button
          data-testid="edit-highlight-button"
          disabled={editingSharedFieldHighlights || isRestricted || disabled}
          Icon={ControlHighlight}
          iconSize="lg"
          linkName={isEditing ? 'Edit Highlight' : 'Add Highlight'}
          onClick={(event) => {
            trackEvent({
              event_name: 'click_action',
              link_name: `Highlight: Open`,
            })
            onClick(field, event)
            processActiveFieldInfo()
          }}
          {...buttonProps}
        >
          {isEditing
            ? t('edit-index.edit-highlight.button', 'Edit Highlight')
            : t('edit-index.add-highlight.button', 'Add Highlight')}
        </Button>
      </div>
      {shouldRenderPanel && activeFieldInfo?.id === parsedField?.id && (
        <HighlightPanelsContainer
          field={field}
          onSave={onSave}
          onCancel={onCancel}
          splitScreenImageArk={splitScreenImageArk}
          saveToSls={saveToSls}
        />
      )}
    </>
  )
}

export default HighlightButton
